import flowWelcomeImg from '../../assets/placeholders/flow_user_guide_welcome_card.png';

// ─────────────────────────────────────────────────────────────────────────────
// flowBuilderTour.js — Workflow Studio Guided Onboarding Tour
//
// AUTO-SKIP ROOT CAUSES AND FIXES:
//   • Step 5 (Sources): waitForAction REMOVED — category expand check is instant.
//   • Steps 17-20 (console tabs): clickOnFocus: true — TourEngine clicks the tab.
//
// KEY FLOW:
//   Step 5 (Sources): spotlight toolbox sources category — explains drag OR Try Sample
//   Step 6 (Try Sample sidebar): spotlight #flow-btn-try-sample-sidebar
//                                 waitForAction: tracks the click + upload completion
//   Step 7 (canvas): once a node is on canvas, user clicks it to open config
//   Step 12 (Run): waitForAction uses __flowTourRunLastFinishedSeq (FlowEditorPage)
//                  so the tour advances only after isRunning returns to false.
// ─────────────────────────────────────────────────────────────────────────────

// ── Tour: flow run lifecycle (FlowEditorPage updates on isRunning transitions) ──
let _tourFlowRunFinishedBaseline = undefined;

// ── Event flag: fired by the sidebar Try Sample button ─────────────────────────
// FlowSidebar.tsx dispatches 'tour:try-sample-sidebar-clicked' on button click.
// This flag is set immediately (before the async upload starts), so the
// TourEngine poll (every ~500 ms) is guaranteed to catch it on the next tick.
let _trySampleSidebarClicked = false;
if (typeof window !== 'undefined') {
  window.addEventListener('tour:try-sample-sidebar-clicked', () => {
    _trySampleSidebarClicked = true;
  });
}

export const getFlowBuilderSteps = () => {
  // Reset flag each time the tour (re)starts
  _trySampleSidebarClicked = false;
  _tourFlowRunFinishedBaseline = undefined;

  return [

  // ───────────────────────────────────────────────────────────────────────────
  // STEP 1 — WELCOME
  // ───────────────────────────────────────────────────────────────────────────
  {
    target: null,
    isWelcome: true,
    image: flowWelcomeImg,
    title: 'Welcome to Workflow Studio',
    content: `Drag nodes, connect them, and run automated workflows — no code needed.`,
  },

  // ───────────────────────────────────────────────────────────────────────────
  // STEP 2 — CREATE A NEW FLOW
  // (list step removed — works even when list is empty)
  // ───────────────────────────────────────────────────────────────────────────
  {
    target: '#flow-list-add-btn',
    title: '➕ Create Your First Flow',
    content: `Click the "ADD FLOW" button to open a blank canvas and start building.

👆 Go ahead and click it now — the tour will continue once the editor opens.`,
    // Advance when the canvas appears in the DOM (editor has loaded)
    waitForAction: () => !!document.querySelector('#flow-canvas-area'),
    skipIfNotFound: true,
  },

  // ───────────────────────────────────────────────────────────────────────────
  // STEP 3 — CANVAS ORIENTATION
  // ───────────────────────────────────────────────────────────────────────────
  {
    target: '#flow-canvas-area',
    title: '🎨 Your Workflow Canvas',
    content: `This is the canvas where you build your workflow visually.

Drag nodes from the left toolbox and drop them here. Connect nodes by dragging from one node's output port (●) to another's input port.

Each connected node passes data to the next step in the flow.`,
    skipIfNotFound: true,
  },

  // ───────────────────────────────────────────────────────────────────────────
  // STEP 4 — NAME YOUR FLOW
  // waitForAction REMOVED — default names like "New Flow 1" already have
  // ≥3 characters, causing the step to auto-advance before the user acts.
  // Now shows as a plain informational step with a Next button.
  // ───────────────────────────────────────────────────────────────────────────
  {
    target: '#flow-editor-name',
    title: '✏️ Name Your Workflow',
    content: `Give your workflow a meaningful name — click the name field and type something like "My First Flow" or "Daily Sales Report".

A clear name makes it easy to find later in your Flows library.`,
    skipIfNotFound: true,
  },

  // ───────────────────────────────────────────────────────────────────────────
  // STEP 5 — TOOLBOX OVERVIEW
  // (search bar step removed — not essential)
  // ───────────────────────────────────────────────────────────────────────────
  {
    target: '#flow-sidebar-toolbox',
    title: '📦 Node Toolbox',
    content: `All your building blocks are here, organised by category:

📥 Sources — pull data from databases, files, APIs
🔀 Transformation — filter, join, aggregate, reshape data
📤 Actions — send emails, write to databases, trigger webhooks
🧠 AI/ML — run AI transforms and predictions
⚡ Control Flow — conditional branching and loops

Every node can be dragged onto the canvas.`,
    skipIfNotFound: true,
  },


  // ───────────────────────────────────────────────────────────────────────────
  // STEP 6 — SOURCES + TRY SAMPLE (combined)
  // footerButton lets users click Try Sample directly from the tour card.
  // waitForAction advances when either: the button is clicked (flag set)
  // OR a react-flow node appears on canvas (manual drag path).
  // ───────────────────────────────────────────────────────────────────────────
  {
    target: '#flow-cat-sources',
    title: '📥 Add a Source Node',
    content: `Expand "Sources" and drag the "File Source" node onto the canvas — or use the button below to instantly load a sample data file.`,
    footerButton: {
      label: '⚡ Try Sample Data',
      onClick: () => {
        // Programmatically click the sidebar button (fires upload + custom event)
        const btn = document.getElementById('flow-btn-try-sample-sidebar');
        if (btn && !btn.disabled) btn.click();
      },
    },
    waitForAction: () =>
      _trySampleSidebarClicked ||
      document.querySelectorAll('.react-flow__node').length > 0,
    skipIfNotFound: true,
  },

  // ───────────────────────────────────────────────────────────────────────────
  // STEP 8 — CLICK THE NODE TO CONFIGURE
  // waitForAction REMOVED — node may be auto-selected after Try Sample upload,
  // making the properties panel already visible → instant skip.
  // ───────────────────────────────────────────────────────────────────────────
  {
    target: '#flow-canvas-area',
    title: '👆 Click the Node to Configure It',
    content: `Your File Source node is now on the canvas! Click it to open the configuration panel on the right.

That's where you'll see the loaded columns, data preview, and can adjust source settings.

👆 Click your node to open the config panel.`,
    // Wait until the properties panel appears — meaning the user clicked a node.
    // This also handles the Try Sample path where the node is auto-selected
    // (panel already visible) — it advances after the 1.2s minimum dwell.
    waitForAction: () => !!document.querySelector('#flow-properties-panel'),
    skipIfNotFound: true,
  },

  // ───────────────────────────────────────────────────────────────────────────
  // STEP 9 — PROPERTIES PANEL
  // Targets the properties panel (always present once a node is selected).
  // If data is already loaded (from sidebar Try Sample), the step just informs
  // and advances with Next. skipIfNotFound: false since panel must be visible.
  // ───────────────────────────────────────────────────────────────────────────
  {
    target: '#flow-properties-panel',
    title: '⚙️ File Source Configuration',
    content: `This is the node configuration panel. For the File Source node you can see:

📋 Loaded columns — all the fields from your data
👁️ Data preview — first few rows to verify the data
📂 Upload zone — to swap in your own file at any time

If you used ⚡ Try Sample, your data is already loaded!

👆 Review the config, then click Next to continue.`,
    // skipIfNotFound: false — required step. TourEngine waits 2s (20 polls × 100ms)
    // instead of giving up after 500ms (5 polls). Prevents auto-skip when the
    // panel is still mounting after a node click or Try Sample upload.
    skipIfNotFound: false,
  },


  // ───────────────────────────────────────────────────────────────────────────
  // STEP 10 — TOOLBAR: SCHEDULE / EXPORT / ROW LIMIT
  // (variables step removed)
  // ───────────────────────────────────────────────────────────────────────────
  {
    target: '#flow-btn-schedule',
    title: '⏰ Schedule, Export & Row Limit',
    content: `A few toolbar tools worth knowing:

📅 Schedule — Set cron-based automatic runs (daily, hourly, etc.)
📥 Export — Download your flow as JSON for backup or sharing
⚡ 1,000 rows — During testing, results are capped at 1,000 rows for speed. Click to change for production full-data runs.`,
    skipIfNotFound: true,
  },

  // ───────────────────────────────────────────────────────────────────────────
  // STEP 11 — SAVE
  // ───────────────────────────────────────────────────────────────────────────
  {
    target: '#flow-btn-save',
    title: '💾 Save Your Flow',
    content: `Always save before running. The yellow dot on the Save button means you have unsaved changes.

Click Save now — the flow will be stored and you'll get a confirmation notification.

👆 Click Save to continue.`,
    // Advance when the Versions button appears (only shown after first save)
    waitForAction: () => !!document.querySelector('#flow-btn-versions'),
    skipIfNotFound: true,
  },

  // ───────────────────────────────────────────────────────────────────────────
  // STEP 12 — RUN
  // waitForAction: FlowEditorPage bumps __flowTourRunLastFinishedSeq when isRunning
  // goes true → false (interactive WS, production WS, or immediate run error).
  // Baseline is captured on first poll so a run finished *before* this step does not skip.
  // ───────────────────────────────────────────────────────────────────────────
  {
    target: '#flow-btn-run',
    title: '▶️ Run Your Flow',
    content: `Click Run to execute your workflow interactively.

Each node will light up in real-time:
🟢 Green — Completed successfully
🔴 Red — Error (check the Errors tab)
🔵 Blue pulsing — Currently running

👆 Click Run — the tour moves on automatically after the run finishes (success or error).`,
    waitForAction: () => {
      if (typeof window === 'undefined') return false;
      const finished = window.__flowTourRunLastFinishedSeq ?? 0;
      if (_tourFlowRunFinishedBaseline === undefined) {
        _tourFlowRunFinishedBaseline = finished;
      }
      return finished > _tourFlowRunFinishedBaseline;
    },
    skipIfNotFound: true,
  },

  // ───────────────────────────────────────────────────────────────────────────
  // STEP 13 — CONSOLE TOGGLE
  // waitForAction REMOVED — if the flow has already run, the console is open
  // and rect.height > 50 is immediately true, causing an instant auto-advance.
  // Now spotlights the toggle button with a plain Next.
  // ───────────────────────────────────────────────────────────────────────────
  {
    target: '#flow-console-toggle',
    title: '📊 Execution Console',
    content: `The Execution Console opened automatically after your run. It has 5 tabs with full details about what just executed.

If it's still closed, click the console bar to open it — the tour continues automatically.`,
    // Console may already be open (auto-opened by the run).
    // waitForAction resolves immediately if so (after 1.2s minimum dwell).
    waitForAction: () => {
      const tab = document.querySelector('#flow-tab-logs');
      if (!tab) return false;
      return tab.getBoundingClientRect().height > 0;
    },
    skipIfNotFound: true,
  },

  // ───────────────────────────────────────────────────────────────────────────
  // STEPS 14-18 — CONSOLE TABS
  // clickOnFocus: true → TourEngine calls el.click() when it locates the tab,
  // which triggers the tab's onClick handler and switches the active tab view.
  // ───────────────────────────────────────────────────────────────────────────
  {
    target: '#flow-tab-logs',
    clickOnFocus: true,
    title: '🖥️ Console — Execution Logs',
    content: `The Console tab shows a real-time log of every step in your execution:

[Info] — Normal progress messages
[Warning] — Non-critical issues
[Error] — Failures that stopped execution

Each log line has a precise timestamp. This is your first stop when debugging a failed run.`,
    skipIfNotFound: false,
  },

  {
    target: '#flow-tab-preview',
    clickOnFocus: true,
    title: '📋 Data Preview',
    content: `Click a node on the canvas, then open the Data Preview tab to see the actual data rows that node produced.

• Up to 1,000 rows shown in a live scrollable table
• Click any cell to expand and copy its full value
• Row count and column count shown at the bottom bar

This is how you verify your transformations are working correctly.`,
    skipIfNotFound: false,
  },

  {
    target: '#flow-tab-io',
    clickOnFocus: true,
    title: '↔️ Input / Output Schema (I/O)',
    content: `The I/O tab shows the column schema flowing in and out of a selected node — without needing to run the flow first.

Color coding tells you what changed:
🟢 Unchanged — column passed through as-is
🔵 Added — new column this node creates
🟡 Renamed — column renamed by this node
❌ Dropped — column removed by this node

Perfect for designing complex transformation pipelines.`,
    skipIfNotFound: false,
  },

  {
    target: '#flow-tab-stats',
    clickOnFocus: true,
    title: '📈 Execution Statistics',
    content: `The Statistics tab shows a performance card for every node that executed:

• Rows processed
• Columns in output
• Execution time (ms)

Use this to spot bottlenecks — if one node takes unusually long, you'll see it here immediately.`,
    skipIfNotFound: false,
  },

  {
    target: '#flow-tab-errors',
    clickOnFocus: true,
    title: '🚨 Errors Tab',
    content: `The Errors tab filters the log to show only error-level messages.

The red badge count on the tab tells you at a glance how many errors occurred. Click any error to read the full message and identify which node caused it.

A clean run shows "No errors" here — that's your green light to publish!`,
    skipIfNotFound: false,
  },

  // ───────────────────────────────────────────────────────────────────────────
  // STEP 19 — PUBLISH
  // ───────────────────────────────────────────────────────────────────────────
  {
    target: '#flow-btn-publish',
    title: '🚀 Publish Your Flow',
    content: `Once your flow runs cleanly, publish it to make it available organisation-wide.

👆 Click Publish to open the publish dialog.`,
    waitForAction: () => !!document.querySelector('[role="dialog"]'),
    skipIfNotFound: true,
  },

  // ───────────────────────────────────────────────────────────────────────────
  // STEP 20 — PUBLISH DIALOG
  // ───────────────────────────────────────────────────────────────────────────
  {
    target: '[role="dialog"]',
    title: '📝 The Publish Modal',
    content: `In this modal you can:
• Set the published name and description
• Choose the version label (e.g. v1.0)
• Assign it to an app or folder

Published flows can be scheduled, event-triggered, or shared with your whole team.

👆 Click "Cancel" (or X) to close this modal and continue.`,
    waitForAction: () => !document.querySelector('[role="dialog"]'),
  },

  // ───────────────────────────────────────────────────────────────────────────
  // STEP 21 — VERSION HISTORY
  // ───────────────────────────────────────────────────────────────────────────
  {
    target: '#flow-btn-versions',
    title: '📜 Version History',
    content: `Every time you save or publish, a version snapshot is created automatically.

Click Versions to:
• Browse all past versions with timestamps and labels
• Load any snapshot back onto the canvas
• Restore a previous state if something breaks

Your work is always safe — nothing is ever permanently lost.`,
    skipIfNotFound: true,
  },

  // ───────────────────────────────────────────────────────────────────────────
  // STEP 22 — FINISH
  // ───────────────────────────────────────────────────────────────────────────
  {
    target: null,
    isWelcome: true,
    title: "You're a Workflow Builder Now! 🎉",
    content: `You've completed the Workflow Studio tour. You're all set to build, run, and publish real workflows.

Happy building!`,
  },
];
};

